import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Eye,
    Users,
    Heart,
    MessageCircle,
    Bookmark,
    Calendar,
    MapPin,
    DollarSign,
    ChevronRight,
    TrendingUp,
    ExternalLink,
    Loader2,
    RefreshCw
} from "lucide-react";
import { getCollabMetrics, getCollabSummary } from "../../../services/api";
import { toast } from "react-toastify";
import RichTextDisplay from "../../common/RichTextDisplay";
import "./CampaignMetrics.scss";

// Utility helpers for data formatting
const formatCompact = (n) => {
    if (n === null || n === undefined) return "0";
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 10000) return `${Math.round(n / 1000)}K`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return `${n}`;
};

const getPerformanceScore = (creator) => (
    creator.metrics.impressions
    || creator.metrics.reach
    || creator.metrics.likes + creator.metrics.comments + creator.metrics.saves
    || creator.engagementRate
);

// Main Component
export default function CampaignMetrics() {
    const { id } = useParams();
    const [activeTab, setActiveTab] = useState("metrics");
    const [expandedCreator, setExpandedCreator] = useState(null);

    const toggleAccordion = (creatorId) => {
        setExpandedCreator(prev => (prev === creatorId ? null : creatorId));
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            "Completada": "badge-success",
            "Pendiente": "badge-warning",
            "En Progreso": "badge-info"
        };
        return <span className={`metrics-badge ${statusMap[status] || "badge-outline"}`}>{status}</span>;
    };

    const StatBox = ({ icon, label, value }) => (
        <div className="stat-box-premium">
            <div className="stat-icon-wrapper">{icon}</div>
            <div className="stat-data">
                <span className="stat-value">{formatCompact(value)}</span>
                <span className="stat-label">{label}</span>
            </div>
        </div>
    );
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [campaign, setCampaign] = useState(null);
    const [metricsData, setMetricsData] = useState(null);

    const fetchData = useCallback(async (isRefresh = false) => {
        if (isRefresh) setRefreshing(true);
        else setLoading(true);

        try {
            const [summaryRes, metricsRes] = await Promise.all([
                getCollabSummary(id),
                getCollabMetrics(id)
            ]);

            setCampaign(summaryRes.data);
            setMetricsData(metricsRes.data);
        } catch (err) {
            console.error("Error fetching campaign metrics:", err);
            toast.error("Error al cargar las métricas de la campaña");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const creators = useMemo(() => {
        if (!metricsData?.posts) return [];
        
        // Group posts by creator
        const creatorMap = {};
        metricsData.posts.forEach(post => {
            const username = post.creator.username;
            if (!creatorMap[username]) {
                creatorMap[username] = {
                    id: username,
                    handle: `@${username}`,
                    avatarUrl: post.creator.profile_picture_url,
                    posts: 0,
                    engagementRate: 0,
                    status: "Completada", // Assumption for now
                    metrics: { impressions: 0, reach: 0, likes: 0, comments: 0, saves: 0 },
                    postsList: []
                };
            }
            
            creatorMap[username].posts += 1;
            creatorMap[username].postsList.push({
                id: post.id,
                caption: `${post.post_type.toUpperCase()}: Ver publicación`,
                url: post.post_url
            });

            // Sum metrics
            const ins = post.insights || {};
            creatorMap[username].metrics.impressions += ins.views || ins.impressions || ins.plays || 0;
            creatorMap[username].metrics.reach += ins.reach || 0;
            creatorMap[username].metrics.likes += ins.likes || 0;
            creatorMap[username].metrics.comments += ins.comments || 0;
            creatorMap[username].metrics.saves += ins.saves || 0;
            
            // Average engagement rate (simplified)
            creatorMap[username].engagementRate = ins.engagement_rate || creatorMap[username].engagementRate;
        });

        return Object.values(creatorMap);
    }, [metricsData]);

    const totals = metricsData?.totals || { views: 0, impressions: 0, reach: 0, likes: 0, comments: 0, saves: 0 };
    const top = creators.length > 0
        ? creators.reduce((acc, c) => (getPerformanceScore(c) > getPerformanceScore(acc) ? c : acc), creators[0])
        : null;
    const completeness = creators.length > 0 ? Math.min(100, Math.round((creators.filter((c) => c.status === "Completada").length / creators.length) * 100)) : 0;

    if (loading) {
        return (
            <div className="campaign-metrics-loading">
                <Loader2 className="animate-spin" size={48} />
                <p>Cargando métricas de campaña...</p>
            </div>
        );
    }

    if (!campaign) {
        return (
            <div className="campaign-metrics-error">
                <p>No se encontró la campaña o no tienes permiso para ver sus métricas.</p>
            </div>
        );
    }

    return (
        <div className="campaign-metrics-container">
            {/* Top Navigation Bar */}
            <div className="metrics-topbar">
                <div className="metrics-topbar-inner">
                    <div className="metrics-breadcrumb">
                        <span className="metrics-brand">FOTH</span>
                        <span className="metrics-path">/ Campañas / {campaign.title}</span>
                    </div>
                    <div className="metrics-topbar-actions">
                        <button className="btn-metrics-ghost" onClick={() => window.history.back()}>Dashboard</button>
                        <button className="btn-metrics-ghost">Mis Collabs</button>
                        <button className="btn-metrics-primary">Nueva Collab</button>
                    </div>
                </div>
            </div>

            <div className="metrics-content-wrapper">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
                    {/* Header Card */}
                    <div className="metrics-header-card">
                        <div className="metrics-header-info">
                            <div className="metrics-header-title-row">
                                <h1 className="metrics-title">{campaign.title}</h1>
                                <span className="metrics-badge badge-active">{campaign.status_display || campaign.status}</span>
                            </div>
                            <div className="metrics-header-meta">
                                <span><TrendingUp size={14} /> {campaign.type_display || "Colaboración"}</span>
                                <span><Calendar size={14} /> {campaign.from_date} – {campaign.to_date}</span>
                                <span><MapPin size={14} /> {campaign.country_name}</span>
                            </div>
                        </div>
                        <div className="metrics-header-actions">
                            <button className="btn-metrics-outline" onClick={() => fetchData(true)} disabled={refreshing}>
                                {refreshing ? <RefreshCw className="animate-spin" size={14} /> : <RefreshCw size={14} />}
                                <span className="ml-2">Actualizar</span>
                            </button>
                            <button className="btn-metrics-primary">Ver entregables</button>
                        </div>
                    </div>

                    {/* Custom Tabs */}
                    <div className="metrics-tabs">
                        <div className="metrics-tabs-list">
                            <button className={`metrics-tab ${activeTab === "details" ? "active" : ""}`} onClick={() => setActiveTab("details")}>Detalles</button>
                            <button className={`metrics-tab ${activeTab === "metrics" ? "active" : ""}`} onClick={() => setActiveTab("metrics")}>Métricas</button>
                            <button className={`metrics-tab ${activeTab === "creators" ? "active" : ""}`} onClick={() => setActiveTab("creators")}>Creadores</button>
                        </div>

                        <div className="metrics-tab-content">
                            {activeTab === "metrics" && (
                                <div className="metrics-grid">
                                    {/* Left Column */}
                                    <div className="metrics-main-col">

                                        {/* Totals Card */}
                                        <div className="metrics-card">
                                            <div className="metrics-card-header flex-between">
                                                <div>
                                                    <h3 className="metrics-card-title">Totales de campaña</h3>
                                                    <p className="metrics-card-desc">Resumen consolidado de publicaciones asociadas.</p>
                                                </div>
                                                <div className="metrics-badges-row">
                                                    <span className="metrics-badge badge-outline">{creators.length} creadores</span>
                                                    <span className="metrics-badge badge-outline">{formatCompact(totals.comments)} comentarios</span>
                                                </div>
                                            </div>
                                            <div className="metrics-card-body">
                                                <div className="stats-grid">
                                                    <StatBox icon={<Eye size={16} />} label="Impresiones" value={totals.impressions} />
                                                    <StatBox icon={<Users size={16} />} label="Alcance" value={totals.reach} />
                                                    <StatBox icon={<Heart size={16} />} label="Likes" value={totals.likes} />
                                                    <StatBox icon={<MessageCircle size={16} />} label="Comentarios" value={totals.comments} />
                                                    <StatBox icon={<Bookmark size={16} />} label="Guardados" value={totals.saves} />
                                                </div>
                                                <hr className="metrics-divider" />
                                                <div className="metrics-progress-section">
                                                    <div className="flex-between">
                                                        <span className="metrics-section-title">Progreso de entregables</span>
                                                        <span className="metrics-progress-hint">{completeness}% completado</span>
                                                    </div>
                                                    <div className="metrics-progress-bar">
                                                        <div className="metrics-progress-fill" style={{ width: `${completeness}%` }}></div>
                                                    </div>
                                                    <p className="metrics-progress-note">
                                                        {creators.filter((c) => c.status === "Completada").length} de {creators.length} creadores completaron.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Top Performer Card */}
                                        {top && (
                                            <div className="metrics-card">
                                                <div className="metrics-card-header">
                                                    <h3 className="metrics-card-title">Top performer</h3>
                                                    <p className="metrics-card-desc">Mejor rendimiento por alcance e impacto total.</p>
                                                </div>
                                                <div className="metrics-card-body">
                                                    <div className="flex-between top-performer-row">
                                                        <div className="creator-info-row">
                                                            <div className="creator-avatar">
                                                                {top.avatarUrl ? <img src={top.avatarUrl} alt={top.handle} /> : <span>{top.handle.replace("@", "").slice(0, 2).toUpperCase()}</span>}
                                                            </div>
                                                            <div className="creator-details">
                                                                <div className="creator-name-row">
                                                                    <span className="creator-handle">{top.handle}</span>
                                                                    <span className="metrics-badge badge-secondary">{top.engagementRate?.toFixed(1)}% engagement</span>
                                                                </div>
                                                                <span className="creator-meta">{top.posts} publicación • {getStatusBadge(top.status)}</span>
                                                            </div>
                                                        </div>
                                                        <button className="btn-metrics-ghost icon-right" onClick={() => toggleAccordion(top.id)}>Ver detalle <ChevronRight size={16} /></button>
                                                    </div>
                                                    <div className="stats-grid stats-grid-spaced">
                                                        <StatBox icon={<Eye size={16} />} label="Impresiones" value={top.metrics.impressions} />
                                                        <StatBox icon={<Users size={16} />} label="Alcance" value={top.metrics.reach} />
                                                        <StatBox icon={<Heart size={16} />} label="Likes" value={top.metrics.likes} />
                                                        <StatBox icon={<MessageCircle size={16} />} label="Comentarios" value={top.metrics.comments} />
                                                        <StatBox icon={<Bookmark size={16} />} label="Guardados" value={top.metrics.saves} />
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Creators Accordion */}
                                        <div className="metrics-card">
                                            <div className="metrics-card-header">
                                                <h3 className="metrics-card-title">Rendimiento por creador</h3>
                                                <p className="metrics-card-desc">Lista compacta y expandible por creador.</p>
                                            </div>
                                            <div className="metrics-card-body">
                                                <div className="metrics-accordion">
                                                    {creators.map((c) => (
                                                        <div className={`accordion-item ${expandedCreator === c.id ? "expanded" : ""}`} key={c.id}>
                                                            <button
                                                              type="button"
                                                              className="accordion-trigger"
                                                              onClick={() => toggleAccordion(c.id)}
                                                              aria-expanded={expandedCreator === c.id}
                                                              aria-controls={`creator-details-${c.id}`}
                                                            >
                                                                <div className="creator-info-row creator-info-row-full">
                                                                    <div className="creator-avatar sm">
                                                                        {c.avatarUrl ? <img src={c.avatarUrl} alt={c.handle} /> : <span>{c.handle.replace("@", "").slice(0, 2).toUpperCase()}</span>}
                                                                    </div>
                                                                    <div className="creator-details creator-details-left">
                                                                        <span className="creator-handle">{c.handle}</span>
                                                                        <span className="creator-meta">{c.posts} publicación • {c.engagementRate?.toFixed(1)}% ER</span>
                                                                    </div>
                                                                </div>
                                                                <div className="accordion-trigger-right">
                                                                    {getStatusBadge(c.status)}
                                                                    <span className="metrics-badge badge-outline">{formatCompact(c.metrics.impressions)} imp.</span>
                                                                </div>
                                                            </button>
                                                            <AnimatePresence>
                                                                {expandedCreator === c.id && (
                                                                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="accordion-content-outer">
                                                                        <div id={`creator-details-${c.id}`} className="accordion-content">
                                                                            <div className="stats-grid">
                                                                                <StatBox icon={<Eye size={16} />} label="Impresiones" value={c.metrics.impressions} />
                                                                                <StatBox icon={<Users size={16} />} label="Alcance" value={c.metrics.reach} />
                                                                                <StatBox icon={<Heart size={16} />} label="Likes" value={c.metrics.likes} />
                                                                                <StatBox icon={<MessageCircle size={16} />} label="Comentarios" value={c.metrics.comments} />
                                                                                <StatBox icon={<Bookmark size={16} />} label="Guardados" value={c.metrics.saves} />
                                                                            </div>
                                                                            <hr className="metrics-divider metrics-divider-spaced" />
                                                                            <div className="accordion-posts">
                                                                                <h4 className="metrics-section-title">Posts asociados</h4>
                                                                                <div className="posts-list">
                                                                                    {c.postsList.map(p => (
                                                                                        <div key={p.id} className="post-item">
                                                                                            <span className="post-caption">{p.caption}</span>
                                                                                            <a href={p.url} target="_blank" rel="noreferrer" className="btn-metrics-ghost icon-right sm">Ver <ExternalLink size={14} /></a>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </motion.div>
                                                                )}
                                                            </AnimatePresence>
                                                        </div>
                                                    ))}
                                                    {creators.length === 0 && (
                                                    <p className="metrics-empty-state">No hay publicaciones registradas aún.</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                    </div>

                                    {/* Right Column */}
                                    <div className="metrics-side-col">
                                        <div className="metrics-card sticky-sidebar">
                                            <div className="metrics-card-header">
                                                <h3 className="metrics-card-title">Resumen</h3>
                                                <p className="metrics-card-desc">Detalles clave de la collab.</p>
                                            </div>
                                            <div className="metrics-card-body">
                                                <div className="summary-list">
                                                    <div className="summary-item">
                                                        <span className="summary-label"><MapPin size={14} /> País</span>
                                                        <span className="summary-value">{campaign.country_name}</span>
                                                    </div>
                                                    <div className="summary-item">
                                                        <span className="summary-label"><Calendar size={14} /> Fechas</span>
                                                        <span className="summary-value">{campaign.from_date} – {campaign.to_date}</span>
                                                    </div>
                                                    <div className="summary-item">
                                                        <span className="summary-label">Requisitos</span>
                                                        <span className="summary-value">{campaign.followers_requirements_display || "No especificado"}</span>
                                                    </div>
                                                </div>
                                                <hr className="metrics-divider" />
                                                <div className="summary-box">
                                                    <span className="metrics-section-title">Audiencia objetivo</span>
                                                    <div className="summary-box-content">{campaign.target_options?.join(" • ") || "Público general"}</div>
                                                </div>
                                                <hr className="metrics-divider" />
                                                <div className="summary-list">
                                                    <div className="summary-item">
                                                        <span className="summary-label"><DollarSign size={14} /> Valor por creador</span>
                                                        <span className="summary-value strong">${parseFloat(campaign.value || 0).toFixed(2)}</span>
                                                    </div>
                                                    <div className="summary-item">
                                                        <span className="summary-label">Plazas contratadas</span>
                                                        <span className="summary-value">{campaign.quantity}</span>
                                                    </div>
                                                    <div className="summary-box-highlight summary-box-highlight-spaced">
                                                        <span className="summary-label">Presupuesto total</span>
                                                        <span className="summary-xl">${(parseFloat(campaign.value || 0) * campaign.quantity).toFixed(0)}</span>
                                                    </div>
                                                </div>
                                                <button className="btn-metrics-primary metrics-action-full" onClick={() => window.location.href = `/business/collabs/${campaign.id}/edit`}>Gestionar campaña</button>
                                            </div>
                                        </div>

                                        <div className="metrics-card metrics-card-spaced">
                                            <div className="metrics-card-header">
                                                <h3 className="metrics-card-title">Notas rápidas</h3>
                                                <p className="metrics-card-desc">Acciones y seguimiento.</p>
                                            </div>
                                            <div className="metrics-card-body">
                                                <div className="notes-list">
                                                    <div className="note-card">
                                                        <span className="note-label">Actualización de métricas</span>
                                                        <span className="note-value">Sincronizado</span>
                                                    </div>
                                                    <div className="note-card">
                                                        <span className="note-label">Posts vinculados</span>
                                                        <span className="note-value">{metricsData?.posts?.length || 0}</span>
                                                    </div>
                                                    <div className="note-card">
                                                        <span className="note-label">Engagement promedio</span>
                                                        <span className="note-value">{creators.length > 0 ? (creators.reduce((t, c) => t + c.engagementRate, 0) / creators.length).toFixed(1) : "0"}%</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === "details" && (
                                <div className="metrics-card">
                                    <div className="metrics-card-header">
                                        <h3 className="metrics-card-title">Detalles</h3>
                                        <p className="metrics-card-desc">Especificaciones de la campaña.</p>
                                    </div>
                                    <div className="metrics-card-body">
                                        <RichTextDisplay content={campaign.description} className="metrics-description" />
                                        <hr className="my-6" />
                                        <h4 className="metrics-section-title metrics-details-heading">Términos y Condiciones</h4>
                                        <RichTextDisplay content={campaign.terms_and_conditions} className="metrics-description" />
                                    </div>
                                </div>
                            )}

                            {activeTab === "creators" && (
                                <div className="metrics-card">
                                    <div className="metrics-card-header">
                                        <h3 className="metrics-card-title">Creadores Seleccionados</h3>
                                        <p className="metrics-card-desc">Lista de creadores participando en esta campaña.</p>
                                    </div>
                                    <div className="metrics-card-body">
                                         <div className="metrics-creators-grid">
                                             {creators.map(c => (
                                                 <div key={c.id} className="metrics-creator-card">
                                                     <div className="metrics-creator-avatar">
                                                         {c.avatarUrl ? <img src={c.avatarUrl} alt={c.handle} /> : <span>{c.handle.substring(1, 3).toUpperCase()}</span>}
                                                     </div>
                                                     <div className="metrics-creator-copy">
                                                         <div className="metrics-creator-name">{c.handle}</div>
                                                         <div className="metrics-creator-meta">{c.posts} posts entregados</div>
                                                     </div>
                                                 </div>
                                             ))}
                                         </div>
                                         {creators.length === 0 && <p className="metrics-empty-state">No hay creadores con entregas registradas aún.</p>}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
